import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

import { env } from "@/config/env";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** True if `value` is an absolute http(s) URL (used to reject malformed env). */
function isAbsoluteHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Base URL of the post-login app, used for auth/transactional email redirect
 * targets so they always land on the app domain.
 *
 * Resolution order (REK-1892):
 *   1. `VITE_WEB_APP_URL` (prod = https://web.reklaimyou.com) — explicit so
 *      email links never depend on where the user happened to be. This matters
 *      because the marketing site now serves the apex + www domains, so
 *      `window.location.origin` could be the marketing host (which has no
 *      /auth or /dashboard routes -> 404 on login links). The value is
 *      validated as an absolute http(s) URL; a malformed var is ignored (and
 *      logged) so we never drive auth redirects from a broken origin — mirrors
 *      the edge `getAppUrl()` validation.
 *   2. In a PRODUCTION build with the var unset/invalid, fall back to the known
 *      app origin (https://web.reklaimyou.com) rather than
 *      `window.location.origin` — a misconfigured prod must never fall through
 *      to the marketing apex/www (the exact 404 this change exists to prevent).
 *   3. `window.location.origin` — staging/dev fallback (app is served from the
 *      same origin there), preserving prior behavior when the env is unset.
 *   4. https://web.reklaimyou.com — non-browser/SSR fallback.
 */
export function getAppBaseUrl(): string {
  const configured = env.webAppUrl;
  if (configured && isAbsoluteHttpUrl(configured)) {
    return configured.replace(/\/+$/, "");
  }
  if (configured) {
    // Non-empty but malformed (missing scheme, stray value): ignore it rather
    // than emit broken auth redirect targets.
    console.error(`getAppBaseUrl: invalid VITE_WEB_APP_URL "${configured}"; falling back`);
  }
  // Production build with no usable app URL: never risk the marketing origin.
  if (import.meta.env.MODE === "production") {
    return "https://web.reklaimyou.com";
  }
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  // Fallback for SSR or non-browser contexts
  return 'https://web.reklaimyou.com';
}

/**
 * Base URL of the public marketing site (the apex), used as the destination
 * when the user leaves the app entirely — e.g. logging out should land on the
 * marketing home, not a now-duplicate page on the app domain.
 *
 * Always returns a bare origin (scheme + host[:port], no path/query/fragment),
 * since callers use it as a navigation target / base.
 *
 * Resolution order (REK-1892), mirroring getAppBaseUrl:
 *   1. `VITE_MARKETING_URL` (prod = https://reklaimyou.com, staging =
 *      https://next.reklaimyou.com) — validated as an absolute http(s) URL and
 *      reduced to its origin; a malformed value is ignored (and logged).
 *   2. In a PRODUCTION build with the var unset/invalid, fall back to the known
 *      apex (https://reklaimyou.com) rather than the app origin.
 *   3. `window.location.origin` — staging/dev fallback (keeps logout on the
 *      current origin when no marketing host is configured).
 *   4. https://reklaimyou.com — non-browser/SSR fallback.
 */
export function getMarketingBaseUrl(): string {
  const configured = env.marketingUrl;
  if (configured && isAbsoluteHttpUrl(configured)) {
    // Enforce the origin contract: drop any stray path/query/fragment so a
    // misconfigured env can't redirect logout to an arbitrary deep link.
    return new URL(configured).origin;
  }
  if (configured) {
    console.error(`getMarketingBaseUrl: invalid VITE_MARKETING_URL "${configured}"; falling back`);
  }
  if (import.meta.env.MODE === "production") {
    return "https://reklaimyou.com";
  }
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'https://reklaimyou.com';
}

/**
 * Base URL of the Next.js app for surviving non-survey handoffs.
 *
 * REK-2456 requires this helper to fail loudly when `VITE_NEXT_APP_URL` is
 * absent or malformed so a misconfigured build cannot silently send users to
 * the production Next data plane.
 */
export function getNextAppBaseUrl(): string {
  const configured = env.nextAppUrl;
  if (configured && isAbsoluteHttpUrl(configured)) {
    return new URL(configured).origin;
  }
  if (configured) {
    console.error(
      `getNextAppBaseUrl: invalid VITE_NEXT_APP_URL "${configured}"`,
    );
  }
  throw new Error("getNextAppBaseUrl: VITE_NEXT_APP_URL must be an absolute http(s) URL");
}
